import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { JURISDICTION_VIEW } from '@rsos/capstone/src/constants/capabilities';
import ErrorPage from '@rsos/components/capstone/ErrorPage';
import AppLoading from '@rsos/components/capstone/Layout/AppLoading';
import Box from '@rsos/components/capstone/base/Box';
import { withLayout } from '@rsos/components/capstone/base/Layout';
import { gspx } from '@rsos/gainsight';
import useSelectedTheme from '@rsos/hooks/useSelectedTheme';
import { initializeHistoricConversations } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp, selectUserPermissionsByApp } from '@rsos/sinatra';
import checkAgent511Access from '@rsos/utils/checkAgent511Access';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import isAgencyShareChatEnabled from '@rsos/utils/isAgencyShareChatEnabled';
import { getProductName } from '@rsos/utils/metaTags';
import useBeforeUnload from '@rsos/utils/useBeforeUnload';
import useBrowserRefresh from '@rsos/utils/useBrowserRefresh';
import initialize from '../actions/initialize';
import JurisdictionView from '../components/JurisdictionView';
import hasAviveCapability from '../components/JurisdictionView/AviveAED/hasAviveCapability';
import hasAxonCapability from '../components/Map/useDeckGL/Axon/hasAxonCapability';
import Hermes from '../components/Websockets/Hermes';
import HermesCallbacks from '../components/Websockets/HermesCallbacks';
import StreamWS from '../components/Websockets/StreamWS';
import StreamWSCallbacks from '../components/Websockets/StreamWSCallbacks';
import { persistor } from '../store';
import canViewAlerts from '../utils/canViewAlerts';
import hasMedicAlertAccess from '../utils/hasMedicAlertAccess';
import useRenewAccessToken from './useRenewAccessToken';
import useRenewProductToken from './useRenewProductToken';

// App-specific scrollbar styling - hidden when not in use
const AppGlobalStyle = createGlobalStyle`
  * {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }
  *::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  *::-webkit-scrollbar-track {
    background: transparent;
  }
  *::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 8px;
  }
  *:hover {
    scrollbar-color: ${({ theme }) => theme.colors.lightLineDivider} transparent;
  }
  *:hover::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.lightLineDivider};
  }
`;

const MainContainer = withLayout(styled(Box).attrs({
  backgroundColor: 'foreground',
})`
  height: ${({ layout }) => layout.body.height}px;
`);
// no op
const useInitialize = (dispatch, org, removedCalls) => {
  useEffect(() => {
    (async () => {
      const action = await dispatch(initialize());
      if (action && action.error) {
        throw new Error(action.error);
      }

      // Initialize historic conversations for Text From 911 after main initialization
      if (org?.name) {
        await dispatch(
          initializeHistoricConversations({ orgName: org.name, removedCalls }),
        );
      }

      // Identify user in Gainsight PX after initialization is complete
      gspx.identify();
    })();
  }, [dispatch, org?.name]);
};

const App = () => {
  const wsRef = useRef();
  const streamWSRef = useRef();

  /**
   * This isRenewingRef is a HACK to prevent excessive re-renders from causing
   * async actions to be dispatched. It behaves like a semaphore for the
   * useRenewProductToken hook to keep a status toggle and prevent requests that
   * already in progress. This is a short term patch and this entire token
   * refresh strategy should be rehauled to match the way Sinatra/Scorpius do
   * refresh renewals and retries (using an interceptor/middleware pattern).
   * Written by E.Flores for hotfix-4.4.0.1.
   */
  const isRenewingRef = useRef(false);
  let location = useLocation();
  const pathName = location?.pathname;
  const sinatra = useSelector(state => state.sinatra);
  const psaps = useSelector(state => state.psaps);

  const org = selectUserOrgByApp(sinatra, 'capstone');
  const { token: accessToken } = useRenewAccessToken(org);
  const productName = getProductName();
  useRenewProductToken(org, productName, isRenewingRef);

  const user = useSelector(state => state.sinatra.user);

  const isUserLoggedIn = user.isLoggedIn;

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const organizationID = useSelector(state => state.sinatra.user.currentOrg.id);
  const applicationName = useSelector(
    state => state.sinatra.user.currentRole.application,
  );
  const themeName = useSelectedTheme();
  const hasAlertsAccess = canViewAlerts(sinatra, psaps);
  const hasHermesV2Access = useSelector(
    state => !!state.psaps.currentPSAP?.activeCapabilities?.hermes_v2_0,
  );

  const isJVEnabled = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP &&
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[JURISDICTION_VIEW]
      ),
  );

  const isAviveEnabled = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP &&
        state.psaps.currentPSAP.activeCapabilities &&
        hasAviveCapability(state.psaps.currentPSAP.activeCapabilities)
      ),
  );

  const isAxonEnabled = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP &&
        state.psaps.currentPSAP.activeCapabilities &&
        hasAxonCapability(state.psaps.currentPSAP.activeCapabilities)
      ),
  );

  const radData = useSelector(
    state =>
      state.emergencyCalls.selectedCallerID &&
      state.emergencyCalls.callQueue[selectedCallerID] &&
      state.emergencyCalls.callQueue[selectedCallerID].additionalData.rad,
  );

  const {
    caller: radCaller,
    devices: radDevices,
    locations: radLocations,
  } = radData || {
    caller: {},
    devices: [],
    locations: [],
  };

  const hasRadData =
    Object.keys(radCaller).filter(key => key !== 'id').length > 0 ||
    radDevices.length > 0 ||
    radLocations.length > 0;

  const permissions =
    user.profile && selectUserPermissionsByApp(user, 'capstone');

  const hasMAaccess = hasMedicAlertAccess(permissions);
  const hasManageAccess = permissions && permissions.includes('MANAGE_USERS');

  const isShareChatEnabled = isAgencyShareChatEnabled(psaps);

  const hasAgent511Access = checkAgent511Access(psaps, sinatra);

  const hasAniAliAccess = checkAniAliAccessSelector({ psaps, sinatra });

  const dispatch = useDispatch();
  const { initialized, initializing, errors } = useSelector(state => state.app);
  const { initializing: initError } = errors;

  const isJVHermesInitialized = useSelector(
    state => state?.emergencyCalls?.isJVHermesInitialized,
  );
  const removedCalls = useSelector(state => state.emergencyCalls.removedCalls);

  useInitialize(dispatch, org, removedCalls);
  useBrowserRefresh();
  useBeforeUnload();

  return useMemo(() => {
    if (initError) {
      return <ErrorPage persistor={persistor} />;
    }

    if (initializing) {
      return (
        <MainContainer>
          <AppLoading />
        </MainContainer>
      );
    }

    if (initialized) {
      return isUserLoggedIn ? (
        <div className={`App rapidsos-${themeName}-theme`}>
          <AppGlobalStyle />
          <MainContainer>
            <Hermes
              accessToken={accessToken}
              applicationName={applicationName}
              hasHermesV2Access={hasHermesV2Access}
              hasMAaccess={hasMAaccess}
              hasAlertsAccess={hasAlertsAccess}
              hasAniAliAccess={hasAniAliAccess}
              isJVEnabled={isJVEnabled}
              isShareChatEnabled={isShareChatEnabled}
              isAviveEnabled={isAviveEnabled}
              isAxonEnabled={isAxonEnabled}
              organizationID={organizationID}
              permissions={permissions}
              pathName={pathName}
              wsRef={wsRef}
            />
            <StreamWS
              accessToken={accessToken}
              applicationName={applicationName}
              hasHermesV2Access={hasHermesV2Access}
              hasMAaccess={hasMAaccess}
              hasAlertsAccess={hasAlertsAccess}
              hasAniAliAccess={hasAniAliAccess}
              isJVEnabled={isJVEnabled}
              isShareChatEnabled={isShareChatEnabled}
              isAviveEnabled={isAviveEnabled}
              isAxonEnabled={isAxonEnabled}
              organizationID={organizationID}
              permissions={permissions}
              pathName={pathName}
              wsRef={streamWSRef}
            />
            {isJVHermesInitialized && <HermesCallbacks />}
            <StreamWSCallbacks />

            <JurisdictionView
              hasAgent511Access={hasAgent511Access}
              hasAlertsAccess={hasAlertsAccess}
              hasManageAccess={hasManageAccess}
              isJVEnabled={isJVEnabled}
              isShareChatEnabled={isShareChatEnabled}
              // TODO - hasRadData is unused, to be deleted by COR-3391
              hasRadData={hasRadData}
              // TODO - wsRef is unused, to be deleted by COR-3391
              wsRef={wsRef}
            />
          </MainContainer>
        </div>
      ) : null;
    }

    return <ErrorPage persistor={persistor} />;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    initializing,
    initError,
    isUserLoggedIn,
    themeName,
    isJVHermesInitialized,
  ]);
};

export default App;
